import {useZoneCapabilitiesQuery} from './useZonesQuery';
import {ZoneWriteCapabilities} from '../types';

// FNP-1191 — THE CAPABILITY-DRIVEN HIDING DECISION, WITHOUT THE CONTROLS.
//
// The write controls themselves are the CRUD half of this ticket and are not
// built: the port methods and the GraphQL mutations do not exist yet. What IS
// built is the decision — so the CRUD ticket drops an Add/Edit/Delete button
// behind `canCreateZones` / `canWriteRecords` / `canDeleteZones` and does not
// have to invent the plumbing or re-derive the default.
//
// ⚠️ THE DEFAULT IS "NOT PERMITTED", IN EVERY UNKNOWN CASE.
//
// Loading, errored, connection missing, connection not ACTIVE, capability
// snapshot absent — all of them hide the control. The alternative fails in the
// worse direction: a control that appears optimistically and then errors at
// submit tells a tenant their read-only credential is broken, when the truth is
// that Flexi should never have offered the action. An absent button is a
// correct statement about a read-only connection; a failing one is not.
//
// The catalog entry (FNP-1179) is the CEILING and this is that ceiling narrowed
// by what the credential actually proved at connect time. A tenant who handed
// Flexi a read-only Route 53 role has canWriteRecords: false even though the
// Route 53 catalog entry says true.
export const useZoneWriteCapabilities = (connectionId?: string) => {

    const {
        data: capabilities,
        isLoading,
        isError,
    } = useZoneCapabilitiesQuery(connectionId);

    // The query resolves null for "no snapshot" (React Query v5 forbids
    // undefined); both mean the same thing to every caller below.
    const resolved: ZoneWriteCapabilities | undefined = capabilities ?? undefined;

    // Not merely `!capabilities`: a snapshot that arrived is still unusable
    // while the query is in flight or after it failed.
    const known = Boolean(connectionId) && !isLoading && !isError && Boolean(resolved);

    return {
        capabilities: resolved,
        // True only when Flexi actually knows what this credential can do.
        capabilitiesKnown: known,
        loading: Boolean(connectionId) && isLoading,
        // ─── What the CRUD follow-up gates its controls on ───
        canCreateZones: known && resolved!.canCreateZones === true,
        canDeleteZones: known && resolved!.canDeleteZones === true,
        canWriteRecords: known && resolved!.canWriteRecords === true,
        canSetNameservers: known && resolved!.canSetNameservers === true,
        // Any write affordance at all. The index header uses this to decide
        // whether a write toolbar is worth rendering.
        canWriteAnything: known && (
            resolved!.canCreateZones === true ||
            resolved!.canDeleteZones === true ||
            resolved!.canWriteRecords === true ||
            resolved!.canSetNameservers === true
        ),
    };
};
